import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextHeadingOneIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-one-icon';
import { RichTextHeadingTwoIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-two-icon';
import { RichTextHeadingThreeIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-three-icon';
import { RichTextHeadingFourIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-four-icon';
import { RichTextHeadingFiveIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-five-icon';
import { RichTextHeadingSixIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-six-icon';
import { richTextIsNodeInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';

export type RichTextLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface RichTextHeadingButtonProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * The heading level.
   */
  level: RichTextLevel;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Whether the button should hide when the heading is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export const richTextHeadingIcons = {
  1: RichTextHeadingOneIcon,
  2: RichTextHeadingTwoIcon,
  3: RichTextHeadingThreeIcon,
  4: RichTextHeadingFourIcon,
  5: RichTextHeadingFiveIcon,
  6: RichTextHeadingSixIcon,
};

export const richTextHeadingShortcutKeys: Partial<
  Record<RichTextLevel, string>
> = {
  1: 'Ctrl-Alt-1',
  2: 'Ctrl-Alt-2',
  3: 'Ctrl-Alt-3',
  4: 'Ctrl-Alt-4',
  5: 'Ctrl-Alt-5',
  6: 'Ctrl-Alt-6',
};

export function richTextCanToggleHeading(
  editor: Editor | null,
  level: RichTextLevel,
): boolean {
  if (!editor) return false;

  try {
    return editor.can().toggleNode('heading', 'paragraph', { level });
  } catch {
    return false;
  }
}

export function richTextIsHeadingActive(
  editor: Editor | null,
  level: RichTextLevel,
): boolean {
  if (!editor) return false;
  return editor.isActive('heading', { level });
}

export function richTextToggleHeading(
  editor: Editor | null,
  level: RichTextLevel,
): void {
  if (!editor) return;

  if (editor.isActive('heading', { level })) {
    editor.chain().focus().setNode('paragraph').run();
  } else {
    editor.chain().focus().toggleNode('heading', 'paragraph', { level }).run();
  }
}

export function richTextIsHeadingButtonDisabled(
  editor: Editor | null,
  level: RichTextLevel,
  userDisabled: boolean = false,
): boolean {
  if (!editor) return true;
  if (userDisabled) return true;
  if (!richTextCanToggleHeading(editor, level)) return true;
  return false;
}

export function richTextShouldShowHeadingButton(params: {
  editor: Editor | null;
  level: RichTextLevel;
  hideWhenUnavailable: boolean;
  headingInSchema: boolean;
}): boolean {
  const { editor, hideWhenUnavailable, headingInSchema } = params;

  if (!headingInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (isNodeSelection(editor.state.selection)) {
      return false;
    }
  }

  return true;
}

export function richTextGetFormattedHeadingName(level: RichTextLevel): string {
  return `Heading ${level}`;
}

export function useRichTextHeadingState(
  editor: Editor | null,
  level: RichTextLevel,
  disabled: boolean = false,
) {
  const headingInSchema = richTextIsNodeInSchema('heading', editor);
  const isDisabled = richTextIsHeadingButtonDisabled(editor, level, disabled);
  const isActive = richTextIsHeadingActive(editor, level);

  const Icon = richTextHeadingIcons[level];
  const shortcutKey = richTextHeadingShortcutKeys[level];
  const formattedName = richTextGetFormattedHeadingName(level);

  return {
    headingInSchema,
    isDisabled,
    isActive,
    Icon,
    shortcutKey,
    formattedName,
  };
}

export const RichTextHeadingButton = React.forwardRef<
  HTMLButtonElement,
  RichTextHeadingButtonProps
>(
  (
    {
      editor: providedEditor,
      level,
      text,
      hideWhenUnavailable = false,
      className = '',
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);

    const {
      headingInSchema,
      isDisabled,
      isActive,
      Icon,
      shortcutKey,
      formattedName,
    } = useRichTextHeadingState(editor, level, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !isDisabled && editor) {
          richTextToggleHeading(editor, level);
        }
      },
      [onClick, isDisabled, editor, level],
    );

    const show = React.useMemo(() => {
      return richTextShouldShowHeadingButton({
        editor,
        level,
        hideWhenUnavailable,
        headingInSchema,
      });
    }, [editor, level, hideWhenUnavailable, headingInSchema]);

    if (!show || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={formattedName}
        aria-pressed={isActive}
        tooltip={formattedName}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextHeadingButton.displayName = 'HeadingButton';

export default RichTextHeadingButton;
